<template>
    <Datepicker
        v-bind="props"
        :locale="locale.locale"
        :class="{ 'with-error': props.isError }"

    >
        <template #input-icon>
            <label v-if="props.placeholder" class="placeholder">
                {{ props.placeholder }}
            </label>
            <Icon :icon="props.icon"/>
        </template>

        <template #clear-icon="{ clear }">
            <IconButton
                icon="close"
                class="tertiary size-s"
                @click="clear"
            />
        </template>

        <template #clock-icon>
            <Icon icon="clock" />
        </template>

        <template #calendar-icon>
            <Icon icon="calendar" />
        </template>

        <template #action-preview="{ value}"></template>

        <template #action-buttons>
            <Button
                :name="t('ui.date-field.save')"
            />
        </template>

        <template #action-row="{ internalModelValue, selectDate }">
            <div class="calendar-action-row">
                <span>{{ timePeriodPreview(internalModelValue, props.timePicker) }}</span>
                <Button
                    class="size-m"
                    :name="t('ui.date-field.apply')"
                    @click="selectDate"
                />
            </div>
        </template>
    </Datepicker>
</template>

<script setup>
    import { useI18n } from 'vue-i18n';
    import IconButton from '🔗/components/ui/IconButton.vue';
    import Button from '🔗/components/ui/Button.vue';
    import moment from 'moment';
    import { timePeriodPreview } from '🔗/utils/utils';
    import stores from '@/stores';

    const locale = stores.locale();
    const { t } = useI18n();

    const props = defineProps({
        placeholder: { type: String, default: '' },
        icon: { type: String, default: 'calendar' },
        timePicker: { type: Boolean, default: true },
        isError: { type: Boolean, default: false }
    });
</script>

<style lang="scss">
    // Datepicker
    .dp__theme_light {
        & > div {
            --dp-background-color: var(--white-500);
            --dp-text-color: var(--dark-500);
            --dp-border-color: var(--gray-20);
            --dp-border-color-hover: var(--primary-500);
            --dp-icon-color: var(--dark-80);
            --dp-danger-color: var(--tertiary-500);
            --dp-primary-color: var(--primary-500);
            --dp-primary-text-color: var(--white-500);
            --dp-font-family: 'Century Gothic';
            --dp-font-size: 14px;

            --dp-hover-color: var(--light-20);
            --dp-hover-text-color: var(--dark-500);
            // --dp-hover-icon-color: #959595;
            // --dp-primary-disabled-color: var();
            // --dp-secondary-color: #c0c4cc;
            // --dp-border-color: #ddd;
            // --dp-menu-border-color: #ddd;
            // --dp-border-color-hover: #aaaeb7;
            // --dp-border-color-focus: #aaaeb7;
            // --dp-disabled-color: #f6f6f6;
            // --dp-scroll-bar-background: #f3f3f3;
            // --dp-scroll-bar-color: #959595;
            // --dp-success-color: #76d275;
            // --dp-success-color-disabled: #a3d9b1;
            // --dp-icon-color: #959595;
            // --dp-danger-color: #ff6f60;
            // --dp-marker-color: #ff6f60;
            // --dp-tooltip-color: #fafafa;
            // --dp-disabled-color-text: #8e8e8e;
            // --dp-highlight-color: rgb(25 118 210 / 10%);
            --dp-range-between-dates-background-color: var(--dp-primary-color);
            --dp-range-between-dates-text-color: var(--dp-primary-text-color);
            --dp-range-between-border-color: var(--dp-primary-color);
            // --dp-loader: 5px solid #1976d2;
        }
    }   

    .dp__main {
        max-width: var(--input-max-width);
        .dp__input_wrap {
            .dp--clear-btn {
                right: 40px;
                padding: 0;
            }

            .dp__input_icon {
                transform: none;
                left: 0;
                right: 0;
                top: 0;
                bottom: 0;
                width: auto;
                height: auto;
                display: flex;
                align-items: center;
                justify-content: end;
                padding: 0 15px 0 12px;
                position: absolute;
                pointer-events: none;

                svg {
                    width: 18px;
                    height: 18px;
                    display: block;
                }
            }

            .dp__input {
                padding: 9px 80px 9px 20px;
                font: var(--font-body-1);
                height: 54px;
                border-radius: 10px;
                color-scheme: var(--color-scheme);

                &::placeholder {
                    color: transparent;
                }

                &.dp_input_focus,
                &:not(:placeholder-shown),
                &:focus {
                    padding-top: 20px;
                    padding-bottom: 8px;
                    border-color: var(--primary-500);

                    & + div > .dp__input_icon {
                        .placeholder {
                            font: var(--font-label);
                            color: var(--primary-500);
                            top: 8px;
                            transform: none;
                        }
                    }
                }
            }
        }

        &.light {
            .dp__input_wrap {
                .dp__input {
                    padding-top: 8px;
                    padding-bottom: 8px;
                    height: 38px;

                    &.dp_input_focus,
                    &:not(:placeholder-shown),
                    &:focus {
                        & + div > .dp__input_icon {
                            .placeholder {
                                display: none;
                            }
                        }
                    }
                }
            }
        }

        &.with-error {
            .dp__input_wrap {
                .dp__input.dp_input_focus, 
                .dp__input:not(:placeholder-shown), 
                .dp__input:focus {
                    border-color: var(--secondary-500);
                }
            }
        }
    }

    .dp__menu {
        background: var(--white-500) !important;
        border: none;
        border: none !important;
        box-shadow: 0px 4px 20px 0px var(--light-80);
        outline: none;

        border-radius: 10px !important;
        overflow: hidden;
        & > div {
            font: var(--font-body-2);
        }

        .dp__calendar {
            background: var(--white-80);
            backdrop-filter: blur(8px);
            -webkit-backdrop-filter: blur(8px);
        }

        .dp__month_year_wrap {
            .dp__overlay {
                .dp__button_bottom {
                    svg {
                        fill: var(--dark-20);
                        stroke: var(--dark-80);
                    }
                }
            }
        }

        .dp__instance_calendar,
        .dp__overlay_container {
            .dp__button,
            .dp__button_bottom {
                svg {
                    fill: var(--dark-80);
                    stroke: var(--dark-500);
                }
            }
        }

        .dp__arrow_top {
            border: none;
        }
    }
</style>

<style lang="scss" scoped>
    .placeholder {
        @include input-placeholder;

        & + svg {
            fill: var(--dark-20);
            stroke: var(--dark-80);
        }
    }

    .calendar-action-row {
        display: flex;
        flex-direction: row;
        width: 100%;
        gap: 8px;
        padding: 0 10px;
        align-items: center;
        span {
            font: var(--font-caption);
            color: var(--dark-500);
            word-wrap: wrap;
            max-width: 150px;
        }
        button {
            flex-shrink: 0;
            margin-left: auto;
        }
    }
</style>